import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ExternalLink, EyeOff, Undo2, Trash2, ListPlus, CheckCheck, RotateCcw } from "lucide-react";
import { mailboxApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/SimpleToast";
import { AssignPopover } from "./AssignPopover";
import { TaskFromEmailDialog } from "./TaskFromEmailDialog";
import { useMailboxTopics } from "./useMailboxTopics";
import { notifyMailboxChanged } from "./useMailboxBadge";

export function MailActions({ email: e, onChanged }) {
  const { user } = useAuth();
  const toast = useToast();
  const topics = useMailboxTopics();
  const [taskOpen, setTaskOpen] = useState(false);
  const canEdit = user?.role !== "readonly";
  const inbox = e.folder !== "sent" && e.status !== "ignored";
  const changed = () => { notifyMailboxChanged(); onChanged?.(); };
  const act = async (fn, msg) => {
    try { await fn(e.id); toast.success(msg); changed(); } catch { toast.error("Aktion fehlgeschlagen"); }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {canEdit && inbox && !e.work_state && (
        <Button size="sm" className="bg-[#1E3A5F] hover:bg-[#142E4A] text-white" onClick={() => setTaskOpen(true)} data-testid={`mail-create-task-${e.id}`}><ListPlus className="w-4 h-4 mr-1.5" />Aufgabe</Button>
      )}
      {canEdit && inbox && !e.work_state && (
        <Button variant="outline" size="sm" className="border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 hover:text-emerald-800" onClick={() => act(mailboxApi.markDone, "Als erledigt markiert")} data-testid={`mail-done-${e.id}`}><CheckCheck className="w-4 h-4 mr-1.5" />Erledigt</Button>
      )}
      {canEdit && inbox && e.work_state === "done" && (
        <Button variant="outline" size="sm" onClick={() => act(mailboxApi.markUndone, "Wieder unbearbeitet")} data-testid={`mail-undone-${e.id}`}><RotateCcw className="w-4 h-4 mr-1.5" />Wieder öffnen</Button>
      )}
      {canEdit && e.status === "open" && <AssignPopover email={e} onAssigned={changed} />}
      {canEdit && e.status === "open" && (
        <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-800" onClick={() => act(mailboxApi.ignore, "Als ignoriert markiert")} data-testid={`mail-ignore-${e.id}`}><EyeOff className="w-4 h-4 mr-1.5" />Ignorieren</Button>
      )}
      {canEdit && e.status !== "open" && (
        <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-800" onClick={() => act(mailboxApi.reopen, "Zurück in „Zuordnung offen“")} data-testid={`mail-reopen-${e.id}`}><Undo2 className="w-4 h-4 mr-1.5" />{e.status === "ignored" ? "Wiederherstellen" : "Zuordnung lösen"}</Button>
      )}
      <span className="flex-1" />
      {e.web_link && (
        <Button variant="outline" size="sm" title="Originalmail in Outlook öffnen" onClick={() => window.open(e.web_link, "_blank", "noopener,noreferrer")} data-testid={`mail-open-${e.id}`}><ExternalLink className="w-4 h-4 mr-1.5" />Outlook</Button>
      )}
      {user?.role === "admin" && (
        <Button variant="ghost" size="icon" className="h-8 w-8" title="Löschen (nur Admin)" onClick={() => window.confirm("Diese E-Mail aus dem CRM löschen? Die Originalmail in Exchange bleibt erhalten.") && act(mailboxApi.remove, "E-Mail gelöscht")} data-testid={`mail-delete-${e.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
      )}
      {taskOpen && <TaskFromEmailDialog email={e} topicName={topics.find((t) => t.id === e.topic_id)?.name} open={taskOpen} onOpenChange={setTaskOpen} onCreated={changed} />}
    </div>
  );
}
